import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import { useRemoveService } from "@/hooks/useApi";
import { useSetDefaultLlm } from "@/pages/Services/useSetDefaultLlm";
import type { Service } from "@/types/api";
import type { LlmDetailsActions } from "./DetailsSection";
import { canDeleteLlm, isLlmDetail } from "./llmDetail";

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : "Failed to delete service";
}

// "Delete service" confirms through the shared modal, removes the service and
// returns to the list.
function useDeleteService(service: Service | undefined) {
  const navigate = useNavigate();
  const { mutateAsync: removeService, isPending: deleting } =
    useRemoveService();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(() => setConfirming(true), []);

  const close = useCallback(() => {
    setConfirming(false);
    setError(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!service) return;
    setError(null);
    try {
      await removeService(service.name);
      navigate("/services");
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [service, removeService, navigate]);

  return { confirming, deleting, error, open, close, confirm };
}

type DeleteState = ReturnType<typeof useDeleteService>;

function LlmDeleteModal({ name, state }: { name: string; state: DeleteState }) {
  return (
    <ConfirmDeleteModal
      isOpen={state.confirming}
      title="Delete service"
      message={
        <>
          Are you sure you want to delete <strong>{name}</strong>? This action
          cannot be undone.
        </>
      }
      loading={state.deleting}
      error={state.error}
      onConfirm={state.confirm}
      onCancel={state.close}
    />
  );
}

// The LLM detail's header actions (Figma 16069-170742): "Set as default"
// switches the Agent's / AI Helper's model in place; "Delete service"
// confirms, removes it and returns to the list. The current default, a lone
// LLM and system services can't be deleted — the Services list's rule.
export function useLlmDetailActions(
  service: Service | undefined,
  llmCount: number,
) {
  const { setDefault, isPending: settingDefault } = useSetDefaultLlm();
  const deletion = useDeleteService(service);

  if (!isLlmDetail(service)) {
    return { llm: undefined, deleteModal: null };
  }

  const llm: LlmDetailsActions = {
    isDefault: service.is_active === true,
    canDelete: canDeleteLlm(service, llmCount),
    settingDefault,
    onSetDefault: () => setDefault(service.name),
    onDelete: deletion.open,
  };

  return {
    llm,
    deleteModal: <LlmDeleteModal name={service.name} state={deletion} />,
  };
}
